import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { User } from '@/types';
import { CasesApi as api } from '@/api';
import { analytics } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/utils/analytics-events';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  googleAuth: () => Promise<void>;
  handleOAuthCallback: () => Promise<{ success: boolean; error?: string }>;
  checkAuthStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      const response: any = await api.getAuthStatus();
      console.log('Auth status response:', response);

      // Accept multiple possible backend shapes:
      // v1: { success: true, data: { ...user } }
      // v2: { success: true, authenticated: true, user: { ... } }
      // v3: { success: true, authenticated: true, data: { user: { ... } } }
      const candidateUser =
        response?.data?.user ||
        response?.user ||
        (response?.data && !response?.data?.user ? response.data : null);

      const authenticated =
        response?.authenticated !== false && !!candidateUser;

      if (response?.success && authenticated && candidateUser) {
        setUser(candidateUser);
      } else if (response?.success && response?.authenticated === true) {
        // Some backends may not return user until another endpoint; keep token & avoid clearing
        console.warn(
          'Auth status indicated authenticated but no user payload; retaining token'
        );
      } else {
        console.warn('Auth status not authenticated; clearing token');
        localStorage.removeItem('auth_token');
        setUser(null);
      }
    } catch (error) {
      console.error('Auth status check failed:', error);
      // Do not immediately nuke token on transient network errors; mark unauthenticated UI but keep token for retry
    } finally {
      setIsLoading(false);
    }
  };

  const exchangeTokenForSession = async (token: string) => {
    try {
      const base =
        (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(
        `${base}/api/integrations/exchange-token?token=${encodeURIComponent(token)}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      );

      if (!response.ok) throw new Error('Token exchange network failure');

      const data: any = await response.json();
      // Normalize shapes
      const issuedToken = data?.token || data?.data?.token;
      const exchangedUser = data?.user || data?.data?.user;

      if (data?.success && issuedToken) {
        localStorage.setItem('auth_token', issuedToken);
        if (exchangedUser) {
          setUser(exchangedUser);
          // Track successful OAuth login and identify user
          analytics.identifyUser(exchangedUser.id, {
            email: exchangedUser.email,
            role: exchangedUser.role || 'user',
            name: exchangedUser.name,
          });
          analytics.track(ANALYTICS_EVENTS.AUTH_LOGIN_SUCCESS, {
            method: 'google',
            user_id: exchangedUser.id,
          });
        }
        return { success: true };
      }

      throw new Error('Token exchange failed: invalid payload');
    } catch (error) {
      console.error('Token exchange error:', error);
      analytics.track(ANALYTICS_EVENTS.AUTH_LOGIN_FAILED, {
        method: 'google',
        error_message: (error as Error).message,
      });
      return { success: false, error: (error as Error).message };
    }
  };

  const handleOAuthCallback = async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const authStatus = urlParams.get('auth');
      const token = urlParams.get('token');

      if (authStatus === 'success' && token) {
        await exchangeTokenForSession(token);
        return { success: true };
      }

      return { success: false, error: 'Invalid OAuth response' };
    } catch (error) {
      console.error('OAuth callback handling failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Authentication failed',
      };
    }
  };

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await api.login(email, password);
      if (response.success) {
        setUser(response.data.user);
        // Store token if provided
        if (response.data.token && typeof window !== 'undefined') {
          localStorage.setItem('auth_token', response.data.token);
        }

        // Track successful login and identify user
        const user = response.data.user;
        analytics.identifyUser(user.id, {
          email: user.email,
          role: user.role || 'user',
          name: user.name,
        });
        analytics.track(ANALYTICS_EVENTS.AUTH_LOGIN_SUCCESS, {
          method: 'email',
          user_id: user.id,
        });
      } else {
        throw new Error('Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      analytics.track(ANALYTICS_EVENTS.AUTH_LOGIN_FAILED, {
        method: 'email',
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      // Track logout before clearing state
      analytics.track(ANALYTICS_EVENTS.AUTH_LOGOUT);
      analytics.resetUser();

      await api.logout();
      localStorage.removeItem('auth_token');
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      // Clear local state anyway
      analytics.track(ANALYTICS_EVENTS.AUTH_LOGOUT);
      analytics.resetUser();
      localStorage.removeItem('auth_token');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const response = await api.googleAuth();
      if (response.success && response.data?.redirectUrl) {
        // Redirect to Google OAuth URL
        window.location.href = response.data.redirectUrl;
      } else {
        throw new Error('Failed to get Google OAuth URL');
      }
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  };

  // Check for OAuth callback or existing session on mount
  useEffect(() => {
    (async () => {
      try {
        const url = new URL(window.location.href);
        const params = url.searchParams;
        const authStatus = params.get('auth');
        const rawToken = params.get('token');
        const nextParam = params.get('next');

        // OAuth bootstrap detection (logs removed in production)

        if (authStatus === 'success') {
          if (rawToken) {
            const result = await exchangeTokenForSession(rawToken);
            if (result.success) {
              await checkAuthStatus();
            } else {
              console.warn('Token exchange reported failure');
            }
          } else {
            console.warn('Auth success without token parameter');
          }

          // Clean params to prevent repeated processing
          params.delete('auth');
          params.delete('token');
          const cleanedPath =
            url.pathname + (params.toString() ? `?${params.toString()}` : '');
          window.history.replaceState({}, document.title, cleanedPath);

          if (localStorage.getItem('auth_token')) {
            const target = nextParam || '/cases';
            if (window.location.pathname !== target) {
              window.location.replace(target);
              return;
            }
          }
          setIsLoading(false);
          return;
        }

        await checkAuthStatus();
      } catch (err) {
        console.error('Auth bootstrap error:', err);
        setIsLoading(false);
      }
    })();
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    googleAuth: handleGoogleLogin,
    handleOAuthCallback,
    checkAuthStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
